import type { ModalProps } from '@workramp/ui';
import type { DateValue, ZonedDateTime } from '@internationalized/date';
import { useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Option,
  OptionGroup,
  Row,
  Select,
  Stack,
} from '@workramp/ui';
import {
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  parseAbsolute,
  startOfMonth,
  startOfWeek,
  today,
} from '@internationalized/date';
import { I18n } from '~/common/explicit-exports';
import { BaseThemeButton } from '~/clc/features/theming/learner/components';

interface MyTeamsReportModalProps extends Omit<ModalProps, 'children'> {
  onClose: () => void;
  onGenerate: ({ fromDate, toDate }: { fromDate: DateValue | null; toDate: DateValue | null }) => void;
  academy: any;
  isOpen: boolean;
}

interface DateRange {
  fromDate: DateValue | null;
  toDate: DateValue | null;
}

type DateRangeDisplay = {
  start: ZonedDateTime | null;
  end: ZonedDateTime | null;
};

const DATE_RANGE_TYPES_OPTIONS = {
  custom: 'custom',
  lastWeek: 'lastWeek',
  lastMonth: 'lastMonth',
};

const CANCEL = I18n.t('js.common.modal.cancel');
const CHOOSE_A_TIME_PERIOD = I18n.t('js.academy.navigation.my_team_reports_modal.choose_a_time_period');
const CUSTOM_DATE_RANGE = I18n.t('js.academy.navigation.my_team_reports_modal.custom_date_range');
const DATE_RANGE = I18n.t('js.academy.navigation.my_team_reports_modal.date_range');
const FROM_DATE = I18n.t('js.academy.navigation.my_team_reports_modal.from_date');
const GENERATE_REPORT = I18n.t('js.academy.navigation.my_team_reports_modal.generate_report');
const LAST_MONTH = I18n.t('js.academy.navigation.my_team_reports_modal.last_month');
const LAST_WEEK = I18n.t('js.academy.navigation.my_team_reports_modal.last_week');
const PREDEFINED_TIME_PERIODS = I18n.t('js.academy.navigation.my_team_reports_modal.predefined_time_periods');
const MY_TEAM_REPORTS_DESCRIPTION = I18n.t('js.academy.navigation.my_team_reports_modal.description');
const MY_TEAM_REPORTS_HEADER = I18n.t('js.academy.navigation.my_team_reports');
const TIME_PERIOD = I18n.t('js.academy.navigation.my_team_reports_modal.time_period');
const TO_DATE = I18n.t('js.academy.navigation.my_team_reports_modal.to_date');

export const MyTeamsReportModal = ({ academy, onClose, onGenerate, ...otherProps }: MyTeamsReportModalProps) => {
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: null,
    toDate: null,
  });

  const isDateRangeValid = dateRange.fromDate && dateRange.toDate && dateRange.fromDate <= dateRange.toDate;

  const [dateRangeType, setDateRangeType] = useState(null);

  const handleSelectChange = (value) => {
    setDateRangeType(value);
    const date = today('UTC');

    if (value === DATE_RANGE_TYPES_OPTIONS.lastWeek) {
      setDateRange({
        fromDate: startOfWeek(date.subtract({ days: 7 }), 'en-US'),
        toDate: endOfWeek(date.subtract({ days: 7 }), 'en-US'),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.lastMonth) {
      setDateRange({
        fromDate: startOfMonth(date.subtract({ months: 1 })),
        toDate: endOfMonth(date.subtract({ months: 1 })),
      });
    } else {
      setDateRange({ fromDate: null, toDate: null });
    }
  };

  const dateRangeDisplayValue: DateRangeDisplay | null =
    dateRange?.fromDate && dateRange?.toDate
      ? {
          start: parseAbsolute(dateRange.fromDate.toDate(getLocalTimeZone()).toISOString(), 'UTC'),
          end: parseAbsolute(dateRange.toDate.toDate(getLocalTimeZone()).toISOString(), 'UTC'),
        }
      : null;

  return (
    <Modal {...otherProps}>
      <ModalHeader title={MY_TEAM_REPORTS_HEADER} description={MY_TEAM_REPORTS_DESCRIPTION} />
      <ModalBody>
        <Stack size={4}>
          <Select
            title={TIME_PERIOD}
            label={TIME_PERIOD}
            onSelectionChange={handleSelectChange}
            placeholder={CHOOSE_A_TIME_PERIOD}
          >
            <OptionGroup aria-label={PREDEFINED_TIME_PERIODS}>
              {[
                { id: DATE_RANGE_TYPES_OPTIONS.lastWeek, label: LAST_WEEK },
                { id: DATE_RANGE_TYPES_OPTIONS.lastMonth, label: LAST_MONTH },
              ].map((option) => (
                <Option key={option.id}>{option.label}</Option>
              ))}
            </OptionGroup>
            <OptionGroup aria-label={CUSTOM_DATE_RANGE}>
              <Option key={DATE_RANGE_TYPES_OPTIONS.custom}>{CUSTOM_DATE_RANGE}</Option>
            </OptionGroup>
          </Select>
          {dateRangeType && (
            <DatePicker
              isRange
              showDayOfWeek={false}
              forceShowYear
              label={DATE_RANGE}
              hideLabel
              startPlaceholder={FROM_DATE}
              endPlaceholder={TO_DATE}
              value={dateRangeDisplayValue}
              onChange={({ start, end }) => setDateRange({ fromDate: start, toDate: end })}
              isDateUnavailable={(val) => val.toDate(getLocalTimeZone()) > new Date()}
              isDisabled={dateRangeType !== 'custom'}
            />
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Row justify="flex-end" gapX={2}>
          <Col xs="auto">
            <Button color="secondary" size="lg" variant="text" onPress={onClose} autoFocus>
              {CANCEL}
            </Button>
          </Col>
          <Col xs="auto">
            <BaseThemeButton
              onPress={() => onGenerate(dateRange)}
              size="lg"
              isDisabled={!isDateRangeValid}
              themeColor={academy.themeOptions.accentColor}
            >
              {GENERATE_REPORT}
            </BaseThemeButton>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
